import React, { useState } from "react";
import { Form, SecondaryButton, NegativeButton } from "common/Forms";
import { InRow, ResponsiveGap } from "common/Grid";
import { TableRenderer } from "common/Table";
import { useNavigate } from "react-router-dom";
import { BiTrash } from "react-icons/bi";
import { ModalDialog } from "common/Dialog";
import { Grid, GridCell } from "common/Grid/Grid";
import { useDeleteStaffUserMutation } from "store/action/staffUserApi";
import { toast } from "react-toastify";
import { LuPencilLine } from "react-icons/lu";
import { PositiveStatusLabel, NegativeStatusLabel } from "common/Label";
import { staffRoleOptions } from "Constant/Constant";
import sortAsc from "assets/img/sort-asc.svg";
import sortDesc from "assets/img/sort-desc.svg";

const SortHeader = ({ label, sortByValue, sorting, onSortChange }) => {
  const isAscActive = sorting?.sortBy === sortByValue && sorting?.sortOrder === 1;
  const isDescActive = sorting?.sortBy === sortByValue && sorting?.sortOrder === 2;
  return (
    <div className="flex items-center justify-between w-full">
      <span>{label}</span>
      <div className="flex items-center gap-1">
        <img
          src={sortAsc}
          alt="sort asc"
          onClick={() => onSortChange?.(sortByValue, 1)}
          className={`cursor-pointer transition-opacity hover:opacity-100 ${isAscActive ? "opacity-100" : "opacity-35"}`}
          style={{ width: 20, height: 20 }}
        />
        <img
          src={sortDesc}
          alt="sort desc"
          onClick={() => onSortChange?.(sortByValue, 2)}
          className={`cursor-pointer transition-opacity hover:opacity-100 ${isDescActive ? "opacity-100" : "opacity-35"}`}
          style={{ width: 20, height: 20 }}
        />
      </div>
    </div>
  );
};

export const TableView = ({ staffUserList, sorting, onSortChange }) => {
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteStaffUser, { isLoading: isDeleting }] = useDeleteStaffUserMutation();

  const onDeleteConfirm = async () => {
    if (!selectedUser || isDeleting) return;

    const payload = {
      staffUserId: selectedUser._id || selectedUser.id,
    };

    try {
      const res = await deleteStaffUser(payload);

      if (res?.data?.status === "SUCCESS") {
        toast.success(res?.data?.message || "Staff user deleted successfully");
        setDeleteModalOpen(false);
        setSelectedUser(null);
      } else {
        toast.error(res?.error?.data?.message || "Something went wrong, please try again.");
        setDeleteModalOpen(false);
      }
    } catch (error) {
      toast.error("Something went wrong, please try again.");
      setDeleteModalOpen(false);
    }
  };

  const onDeleteClick = (user) => {
    if (isDeleting) return;
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  return (
    <Form>
      <TableRenderer
        data={staffUserList ?? []}
        columns={[
          {
            name: "Full Name",
            widthRequest: { min: 220 },
            headerRenderer: () => <SortHeader label="Full Name" sortByValue={1} sorting={sorting} onSortChange={onSortChange} />,
            bodyRenderer: x => x?.fullName ? x.fullName.charAt(0).toUpperCase() + x.fullName.slice(1) : <div>-</div>
          },
          {
            name: "Email",
            widthRequest: { min: 220 },
            headerRenderer: () => <SortHeader label="Email" sortByValue={2} sorting={sorting} onSortChange={onSortChange} />,
            bodyRenderer: (x) => x?.email || <div>-</div>,
          },
          {
            name: "Username",
            widthRequest: { min: 200 },
            headerRenderer: () => <SortHeader label="Username" sortByValue={3} sorting={sorting} onSortChange={onSortChange} />,
            bodyRenderer: (x) => x?.username || <div>-</div>,
          },
          {
            name: "Phone",
            widthRequest: { min: 150 },
            bodyRenderer: (x) => x?.phoneNumber || <div>-</div>,
          },
          {
            name: "Role",
            widthRequest: { min: 150 },
            bodyRenderer: (x) => staffRoleOptions.find((r) => r.value === x?.role)?.label || <div>-</div>,
          },
          {
            name: "Site ID",
            widthRequest: { min: 150 },
            bodyRenderer: (x) => x?.siteid || <div>-</div>,
          },
          {
            name: "Status",
            widthRequest: { min: 120 },
            bodyRenderer: (x) =>
              x?.isActive ? (
                <PositiveStatusLabel title="Active" />
              ) : (
                <NegativeStatusLabel title="Inactive" />
              ),
          },
          {
            name: "Actions",
            widthRequest: { min: 150 },
            classNames: ["!text-center"],
            headerRenderer: (col) => <div className="w-full text-center">{col.name}</div>,
            bodyRenderer: (x) => (
              <InRow gap={1} classNames={["justify-center"]}>
                <SecondaryButton
                  disabled={!x?.isActive}
                  action={() => navigate(`/edit-staff-user`, { state: { user: x } })}
                  title="Edit Staff User">
                  <LuPencilLine size={20} />
                </SecondaryButton>
                <NegativeButton
                  disabled={!x?.isActive}
                  action={() => onDeleteClick(x)}
                  title="Delete Staff User">
                  <BiTrash size={20} />
                </NegativeButton>
              </InRow>
            ),
          },
        ]}
      />

      <ModalDialog isOpened={deleteModalOpen} title="Confirm Delete">
        <div className="p-5 text-center">Are you sure you want to delete this staff user?</div>
        <ResponsiveGap left={false} right={false}>
          <Grid columns={2}>
            <GridCell>
              <NegativeButton isWide={true} action={onDeleteConfirm} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </NegativeButton>
            </GridCell>
            <GridCell>
              <SecondaryButton
                disabled={isDeleting}
                action={() => {
                  setDeleteModalOpen(false);
                  setSelectedUser(null);
                }}
                isWide={true}>
                Cancel
              </SecondaryButton>
            </GridCell>
          </Grid>
        </ResponsiveGap>
      </ModalDialog>
    </Form>
  );
};
